import React, { SyntheticEvent, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { nameMaxLength, taskTypes, aspectRatio as ar } from "../../../../common/parameters";
import { ExperienceOrder, ExperienceTheme } from "../../../../common/types";

import { IUser } from "../../../store/user/models";
import { get, post } from "../../../actions/general";
import { IStateTree } from "../../../store/interfaces";
import { editUser } from "../../../store/user/actions";
import Form, {
    Title,
    InputText,
    Label,
    Select,
    Checkbox,
    Submit,
    Validation,
    InputTextArea,
    Radio,
} from "../../../components/Form";
import DateTime from "../../../components/DateTime";
import Alert from "../../../components/Alert";
import Draggable from "../../../components/Draggable";
import Icons from "../../../icons";
import Icon from "../../../icon";
import routing from "../../../routing";
import { ICheckboxEvent, IRadioEvent, ISelectEvent } from "../../../types";

import { IClass } from "../../homeworks/interfaces";
import { Screens, Screen, borderWidth, IconScalerDrag, Difficulty } from "../ExperienceEditor/styles";
import { screenPositions, mainScreenPosition, getTaskScores } from "../ExperienceEditor/utils";
import { defaultIconWidth, getScoreValue } from "../ExperienceEditor/ExperienceEditor";

import { SubmitWrapper, TaskList, ArrowButton, Buttons, AlreadyCreatedClassList } from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";
import { getDragPositions, parseClasses } from "./utils";


const getDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(10);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
}

const NewHomeworkModal = ({ isSimulation, tasks, planId, hasExperience, plan }: IProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const user = useSelector((state: IStateTree) => state.user) as IUser;
    const navigate = useNavigate();
    const [isPrivacy, changeIsPrivacy] = useState(false);

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [isExperience, setIsExperience] = useState(false);
    const [date, setDate] = useState(getDate());
    const [startDate, setStartDate] = useState(new Date);
    const [classId, setClassId] = useState("");
    const [classes, setClasses] = useState<IClass[]>([]);
    const [className, setClassName] = useState("");
    const [addedClasses, setAddedClasses] = useState<string[]>([]);
    const [isRepeat, setIsRepeat] = useState(false);
    const [selectedTasks, setSelectedTasks] = useState(!isSimulation ? tasks.map(task => task.id) : []);
    const [skippedExperienceTasks, setSkippedExperienceTasks] = useState<number[]>([]);
    const [theme, setTheme] = useState<ExperienceTheme | undefined>(plan?.experience?.theme);
    const [order, setOrder] = useState<ExperienceOrder>(plan?.experience?.order || ExperienceOrder.Fix);
    const [score, setScore] = useState<any>(plan?.experience?.score);
    const [customizeExperience, setCustomizeExperience] = useState(false);

    const [validations, setValidations] = useState<{ [prop: string]: null | string }>({
        title: null,
        className: null,
        classId: null,
        date: null,
    })
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!isSimulation) {
            dispatch(get("teacher-classes", (res: { data: IClass[] }) => {
                setClasses(res.data)
            }));
        }
    }, [])

    const handleChange = (change: { [prop: string]: string | boolean }) => {
        if (change.title && (change.title as string).length >= 40) return;
        if (change.className && (change.className as string).length >= 40) return;
        if (change.title !== undefined) {
            setTitle(change.title as string);
        } else if (change.description !== undefined) {
            setDescription(change.description as string);
            return;
        } else if (change.isExperience !== undefined) {
            setIsExperience(change.isExperience as boolean)
            if (change.isExperience) {
                setSelectedTasks(tasks.map(task => task.id));
            }
        } else if (change.classId !== undefined) {
            setClassId(change.classId as string);
            setClassName("");
        } else if (change.className !== undefined) {
            setClassName(change.className as string);
        }
        setValidations(oldValidations => {
            const newValidation = { ...oldValidations };
            newValidation[Object.keys(change)[0]] = null;
            return newValidation;
        });
    };

    const changeDate = (date: Date) => {
        setDate(date);
        setValidations(oldValidations => {
            const newValidation = { ...oldValidations };
            newValidation.date = null;
            if (startDate.getTime() > date.getTime()) {
                newValidation.startDate = "mustBeOlder";
            } else {
                newValidation.startDate = null;
            }
            return newValidation;
        })
    };

    const changeStartDate = (startDate: Date) => {
        setStartDate(startDate);
        setValidations(oldValidations => {
            const newValidation = { ...oldValidations };
            newValidation.startDate = null;
            if (startDate.getTime() > date.getTime()) {
                newValidation.startDate = "mustBeOlder";
            }
            return newValidation;
        })
    };

    const submit = () => {
        if (title.length === 0) {
            setValidations(oldValidations => {
                const newValidation = { ...oldValidations };
                newValidation.title = "required";
                return newValidation;
            });
            return;
        }
        if (date.getTime() < new Date().getTime()) {
            setValidations(oldValidations => {
                const newValidation = { ...oldValidations };
                newValidation.date = "mustBeLater";
                return newValidation;
            });
            return;
        }
        if (date.getTime() <= startDate.getTime()) {
            return;
        }
        if (!classId) {
            setValidations(oldValidations => {
                const newValidation = { ...oldValidations };
                newValidation.classId = "required";
                return newValidation;
            });
            return;
        }
        if (classId === "new" && className.length === 0) {
            setValidations(oldValidations => {
                const newValidation = { ...oldValidations };
                newValidation.className = "required";
                return newValidation;
            });
            return;
        }
        if (classId === "new" && nameMaxLength < className.length) {
            setValidations(oldValidations => {
                const newValidation = { ...oldValidations };
                newValidation.className = "tooLongClassName";
                return newValidation;
            });
            return;
        }
        setLoading(true);
        dispatch(
            post(
                "create-homework",
                {
                    title,
                    date: date.getTime(),
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    timeZoneOffset: date.getTimezoneOffset(),
                    classId,
                    className,
                    planId,
                    description,
                    selectedTasks,
                    isExperience,
                    startDate: startDate.getTime(),
                    skippedExperienceTasks,
                    theme,
                    order,
                    score
                },
                (res: { data: { key: number, _id: string } }) => {
                    dispatch(editUser({ ...user, key: res.data.key }));
                    if (isRepeat) {
                        setClassId("");
                        setLoading(false);
                        setClassName("");
                        setAddedClasses(oldAddedClasses => oldAddedClasses.concat(className || classId))
                    } else {
                        navigate("/" + routing.homeworkResult + "/" + res.data._id);
                    }
                },
                () => {
                    setLoading(false);
                }
            )
        );
    };

    const changeSelectedTasks = (taskId: number, isAdd: boolean) => {
        if (!isAdd) {
            setSelectedTasks(oldSelectedTasks =>
                oldSelectedTasks.filter(id => {
                    if (id === taskId) {
                        return false;
                    }
                    return true;
                })
            );
        } else {
            setSelectedTasks(oldSelectedTasks =>
                [...oldSelectedTasks, taskId]
            );
        }
    };

    const changeOrder = (e: SyntheticEvent, taskIndex: number, dir: -1 | 1) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedTasks(oldSelectedTasks => {
            const newSelectedTasks = [...oldSelectedTasks];
            const movingTask = newSelectedTasks.splice(taskIndex, 1);
            newSelectedTasks.splice(taskIndex + dir, 0, movingTask[0]);
            return newSelectedTasks;
        });
    };

    const changeIsRepeat = ({ target: { checked } }: ICheckboxEvent) => {
        setIsRepeat(checked);
    };

    const screenWidth = 100;
    const screenHeight = screenWidth * (ar.height / ar.width);
    const iconWidth = screenWidth / 3;

    const dragPositions = getDragPositions({ plan, screenWidth, screenHeight });

    const handleSelectExperienceTask = (id: number) => {
        if (skippedExperienceTasks.includes(id)) {
            setSkippedExperienceTasks(old => old.filter(i => i !== id));
        } else {
            let totalTasks = 0;
            plan!.experience.positions.filter(position => {
                if (typeof position === "number") {
                    if (!skippedExperienceTasks.includes(position)) {
                        totalTasks++;
                    }
                } else if (Array.isArray(position)) {
                    position.forEach(p => {
                        if (!skippedExperienceTasks.includes(p)) {
                            totalTasks++;
                        }
                    })
                }
            });
            if (totalTasks <= 1) return;
            setSkippedExperienceTasks(old => [...old, id]);
        }
    };

    const handleChangeTheme = ({ target: { value } }: ISelectEvent<ExperienceTheme>) => {
        setTheme(value);
    };
    const handleChangeOrder = ({ target: { value } }: IRadioEvent) => {
        setOrder(value);
    };
    const handleScoreChange = ({ target: { value } }: ISelectEvent<string | number>) => {
        setScore(value);
    };

    const handleChangeCustomizeExperience = () => {
        if (customizeExperience) {
            setSkippedExperienceTasks([]);
            setTheme(plan?.experience?.theme)
            setOrder(plan?.experience?.order || ExperienceOrder.Fix)
            setScore(plan?.experience?.score)
        }
        setCustomizeExperience(old => !old);
    }

    return (
        <>
            <Title>
                <FormattedMessage {...messages.title} />
            </Title>
            <Form disabled={loading}>
                <InputText
                    focusOnMount={!isSimulation}
                    validation={validations.title}
                    required
                    label={formatMessage(messages.homeworkTitle)}
                    value={title}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => handleChange({ title: e.currentTarget.value })}
                />
                <InputTextArea
                    name="description"
                    label={formatMessage(messages.homeworkDescription)}
                    value={description}
                    onChange={(e: React.FormEvent<HTMLTextAreaElement>) => handleChange({ description: e.currentTarget.value })}
                    maxLength={500}
                />
                {hasExperience && (
                    <Checkbox
                        label={formatMessage(messages.experience)}
                        checked={isExperience}
                        onChange={(e: ICheckboxEvent) => handleChange({ isExperience: e.target.checked })}
                    />
                )}
                {!isSimulation && !isExperience && (
                    <>
                        <Label>
                            <FormattedMessage {...messages.tasks} />
                        </Label>
                        <TaskList>
                            {selectedTasks.map((taskId, taskIndex) => {
                                const task = tasks.find(task => task.id === taskId)!;
                                const TaskIcon = Icons[task.type];
                                return (
                                    <Checkbox
                                        key={taskId}
                                        checked
                                        disabled={selectedTasks.length === 1}
                                        onChange={() => changeSelectedTasks(taskId, false)}
                                        label={
                                            <div>
                                                <TaskIcon active />
                                                {taskTypes(formatMessage)[task.type]}
                                                <Buttons>
                                                    <ArrowButton
                                                        className={cn({ Invisible: taskIndex === 0 })}
                                                        onClick={(e: SyntheticEvent) => changeOrder(e, taskIndex, -1)}
                                                    >
                                                        <Icon i="arrowup" />
                                                    </ArrowButton>
                                                    <ArrowButton
                                                        className={cn("Flip", {
                                                            Invisible: taskIndex === selectedTasks.length - 1,
                                                        })}
                                                        onClick={e => changeOrder(e, taskIndex, 1)}
                                                    >
                                                        <Icon i="arrowup" />
                                                    </ArrowButton>
                                                </Buttons>
                                            </div>
                                        }
                                    />
                                );
                            })}

                            {tasks.map(({ id, type }) => {
                                if (selectedTasks.indexOf(id) !== -1) return null;
                                const TaskIcon = Icons[type];
                                return (
                                    <Checkbox
                                        name={`task_${id}`}
                                        key={id}
                                        checked={false}
                                        onChange={() => changeSelectedTasks(id, true)}
                                        label={
                                            <div>
                                                <TaskIcon />
                                                {taskTypes(formatMessage)[type]}
                                            </div>
                                        }
                                    />
                                );
                            })}
                        </TaskList>
                    </>
                )}
                {isExperience && (
                    <Checkbox
                        label={formatMessage(messages.customizeExperience)}
                        checked={customizeExperience}
                        onChange={handleChangeCustomizeExperience}
                    />
                )}
                {isExperience && customizeExperience && (
                    <>
                        <Label>
                            <FormattedMessage {...messages.tasks} />
                        </Label>
                        <Screens style={{
                            height: screenHeight * 4,
                            width: screenWidth! * 3 - borderWidth * 2,
                        }}>
                            {screenPositions.map(({ x, y }, index) => {
                                let Active = false;
                                if (typeof plan!.experience.positions[index] === "number") {
                                    if (!skippedExperienceTasks.includes(plan!.experience.positions[index] as number)) {
                                        Active = true;
                                    }
                                } else if (Array.isArray(plan!.experience.positions[index])) {
                                    if (!skippedExperienceTasks.includes((plan!.experience.positions[index] as number[])[0]) || !skippedExperienceTasks.includes((plan!.experience.positions[index] as number[])[1])) {
                                        Active = true;
                                    }
                                }
                                return (
                                    <Screen
                                        key={index}
                                        className={cn({ Active })}
                                        style={{
                                            width: screenWidth!,
                                            height: screenHeight,
                                            left: x * screenWidth! - x * borderWidth,
                                            top: y * screenHeight - y * borderWidth,
                                        }}
                                    />
                                )
                            })}
                            <Screen
                                className="StartScreen"
                                style={{
                                    width: screenWidth!,
                                    height: screenHeight,
                                    top: mainScreenPosition.y * screenHeight - mainScreenPosition.y * borderWidth,
                                    left: mainScreenPosition.x * screenWidth! - mainScreenPosition.x * borderWidth,
                                }}
                            />
                            {plan!.tasks.map(({ type, id }, index) => {
                                const TaskIcon = Icons[type];
                                const position = {
                                    ...dragPositions![index],
                                };
                                if (position.x === -1) return null;
                                let difficulty: null | "Hard" | "Easy" = null;
                                plan!.experience.positions.forEach(position => {
                                    if (Array.isArray(position)) {
                                        if (position[0] === id) {
                                            difficulty = "Easy";
                                        } else if (position[1] === id) {
                                            difficulty = "Hard";
                                        }
                                    }
                                })
                                return (
                                    <Draggable
                                        key={index}
                                        position={position}
                                        onDrag={() => null}
                                        onDragStart={() => null}
                                        onStop={() => null}
                                        zIndex={1}
                                        onClick={() => handleSelectExperienceTask(id)}
                                    >
                                        <IconScalerDrag
                                            style={{
                                                width: iconWidth,
                                                height: iconWidth,
                                                transform: `scale(${iconWidth / defaultIconWidth})`
                                            }}
                                        >
                                            <TaskIcon active={!skippedExperienceTasks.includes(id)} />
                                            {difficulty && (
                                                <Difficulty className={difficulty}>
                                                    <Icon i="chevron-right" />
                                                </Difficulty>
                                            )}
                                        </IconScalerDrag>
                                    </Draggable>
                                );
                            })}
                        </Screens>
                        <Radio
                            name="order"
                            label={<FormattedMessage {...messages.order} />}
                            value={order}
                            options={[
                                {
                                    value: ExperienceOrder.Fix,
                                    label: formatMessage(messages.fix)
                                },
                                {
                                    value: ExperienceOrder.Free,
                                    label: formatMessage(messages.free)
                                },
                            ]}
                            onChange={handleChangeOrder}
                        />
                        {!theme && (
                            <Select<string | number | null>
                                noDelete
                                showNull
                                value={getScoreValue(score)}
                                label={formatMessage(messages.scoreLabel)}
                                options={[
                                    { value: null, label: formatMessage(messages.coverImage) },
                                    ...(plan!.scores || []).map((score, index) => ({
                                        value: score._id,
                                        label: formatMessage(messages.scoreOptionScore, { index: index + 1 }),
                                    })),
                                    ...getTaskScores(plan!).map(({ value, label }) => {
                                        return {
                                            value,
                                            label: formatMessage(messages.scoreOptionTaskScore, { index: label.index + 1, type: formatMessage({ id: `taskTypes.${label.type}` }) }),
                                        }
                                    }),
                                ]}
                                name="score"
                                onChange={handleScoreChange}
                            />
                        )}
                        <Select
                            noDelete
                            showNull
                            value={theme || null}
                            label={formatMessage(messages.theme)}
                            name="theme"
                            onChange={handleChangeTheme}
                            options={[
                                {
                                    value: null,
                                    label: formatMessage(messages.basic)
                                },
                                {
                                    value: ExperienceTheme.Easter,
                                    label: formatMessage(messages.easter)
                                },
                                {
                                    value: ExperienceTheme.Christmas,
                                    label: formatMessage(messages.christmasTuning)
                                }
                            ]}
                        />
                    </>
                )}
                <Label required>
                    <FormattedMessage {...messages.homeworkStartDate} />
                </Label>
                {validations.startDate && <Validation validation={validations.startDate} />}
                <DateTime date={startDate} onChange={changeStartDate} disabled={loading} />
                <Label required>
                    <FormattedMessage {...messages.homeworkDate} />
                </Label>
                {validations.date && <Validation validation={validations.date} />}
                <DateTime date={date} onChange={changeDate} disabled={loading} />
                {!isSimulation && (
                    <Alert>
                        <FormattedMessage {...messages.deadlineAlert} />
                    </Alert>
                )}
                <Select
                    label={<FormattedMessage {...messages.classLabel} />}
                    value={classId}
                    required
                    name="class"
                    validation={validations.classId}
                    options={[
                        {
                            label: formatMessage(messages.newClass),
                            value: "new",
                        },
                        ...parseClasses(classes, addedClasses, formatMessage(messages.addedClass)),
                    ]}
                    onChange={e => handleChange({ classId: e.target.value })}
                    noDelete
                    disabled={loading}
                />
                {addedClasses.length > 0 && (
                    <AlreadyCreatedClassList>
                        <FormattedMessage {...messages.addedClasses} />: {addedClasses.map(addedClass => {
                            const addedCl = classes.find(cl => cl._id === addedClass);
                            if (addedCl) {
                                return addedCl.name
                            }
                            return addedClass;
                        }).join(", ")}
                    </AlreadyCreatedClassList>
                )}
                {classId === "new" && (
                    <InputText
                        validation={validations.className}
                        required
                        label={formatMessage(messages.newClassName)}
                        value={className}
                        name="new_class"
                        onChange={(e: React.FormEvent<HTMLInputElement>) => handleChange({ className: e.currentTarget.value })}
                    />
                )}
                <Checkbox
                    name="repeat"
                    checked={isRepeat}
                    onChange={changeIsRepeat}
                    label={formatMessage(messages.repeatCreating)}
                    disabled={loading}
                />
                <Checkbox
                    name="privacy"
                    checked={isPrivacy}
                    onChange={() => changeIsPrivacy(!isPrivacy)}
                    label={formatMessage(messages.privacy)}
                    disabled={loading}
                />
                <SubmitWrapper>
                    <Submit onClick={submit} loading={loading} disabled={!isPrivacy}>
                        <FormattedMessage {...messages.create} />
                    </Submit>
                </SubmitWrapper>
            </Form>
        </>
    );
}

export default NewHomeworkModal;
